import { Alert, Typography } from 'antd'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoLocale } from 'features/locale'
import { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { ExecutionResultsTable } from './ExecutionResultsTable'
import { ExecutionScopeSelector } from './ExecutionScopeSelector'
import {
  ExecutionRow,
  executionRowsSchema,
  ExecutionSection,
} from './executionTypes'
import {
  appendInspectionResultRows,
  createInspectionResultRows,
  readInspectionExecution,
} from './inspectionResultsModel'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`

export const ExecutionSectionView = ({
  config,
  fieldValues,
  setFieldValues,
}: {
  config: ExecutionSection
  fieldValues: FieldValues
  setFieldValues: Dispatch<SetStateAction<FieldValues>> | undefined
}) => {
  const { globalContext } = useWorkflow()
  const { measurementSystem } = useGeckoLocale()
  const execution = readInspectionExecution(config, fieldValues, globalContext)
  if (!execution.success)
    return (
      <Alert
        type='warning'
        showIcon
        title='Inspection execution needs attention'
        description='The source assessment, inspection areas, results or workflow settings could not be read. This section cannot be completed until they are corrected.'
      />
    )
  const { zones, areas, rawZones, rows, strategies, catalogue } = execution.data
  const update = (resolve: (previous: ExecutionRow[]) => ExecutionRow[]) =>
    setFieldValues?.((previous) => {
      const current = executionRowsSchema.safeParse(
        unwrapFieldValue(previous[config.field]) ?? []
      )
      if (!current.success) return previous
      return {
        ...previous,
        [config.field]: wrapFieldValue(
          resolve(current.data),
          getFieldSource(previous[config.field])
        ),
      }
    })
  return (
    <Container>
      {setFieldValues && (
        <ExecutionScopeSelector
          zones={zones}
          areas={areas}
          catalogue={catalogue}
          rows={rows}
          allowRepeated={config.allowRepeatedMethods}
          onAdd={(zone, area, methods) =>
            update((previous) =>
              appendInspectionResultRows(
                previous,
                createInspectionResultRows({
                  zone,
                  area,
                  methods,
                  columns: config.columns,
                  rawZone: rawZones.find((item) => item.id === zone.id) ?? {},
                  strategies,
                  catalogue,
                  newId: v4,
                }),
                config.allowRepeatedMethods
              )
            )
          }
        />
      )}
      <Typography.Text type='secondary'>
        One row per method. Expand a row to view its procedure and enter
        inspection details.
      </Typography.Text>
      <ExecutionResultsTable
        rows={rows}
        columns={config.columns}
        zones={zones}
        areas={areas}
        catalogue={catalogue}
        rawZones={rawZones}
        system={measurementSystem}
        readonly={!setFieldValues}
        onChange={(id, field, value) =>
          update((previous) =>
            previous.map((row) =>
              row.id === id
                ? { ...row, values: { ...row.values, [field]: value } }
                : row
            )
          )
        }
        onRemove={(id) =>
          update((previous) => previous.filter((row) => row.id !== id))
        }
      />
    </Container>
  )
}
